import { ref, computed, watch } from 'vue'
import { useApi } from './useApi.js'

// ── Layout constants ─────────────────────────────────────────────────────────
export const TIME_COL_WIDTH = 40
export const COL_WIDTH      = 150

// ── Resizable row height (shared across Day + Week views, persisted) ────────
const MIN_HOUR_HEIGHT  = 50
const MAX_HOUR_HEIGHT  = 220
const HOUR_HEIGHT_KEY  = 'bma_calendar_hour_height'

const storedHourHeight = Number(localStorage.getItem(HOUR_HEIGHT_KEY))
const hasStoredHourHeight = storedHourHeight >= MIN_HOUR_HEIGHT && storedHourHeight <= MAX_HOUR_HEIGHT
export const hourHeight = ref(hasStoredHourHeight ? storedHourHeight : 100)

export const setHourHeight = px => {
  hourHeight.value = Math.min(MAX_HOUR_HEIGHT, Math.max(MIN_HOUR_HEIGHT, Math.round(px)))
  localStorage.setItem(HOUR_HEIGHT_KEY, String(hourHeight.value))
}

// One-shot: on first-ever load (no saved zoom yet), pick a height that fills the panel
// instead of the arbitrary 100px default. Never fires again once a value exists.
let hourHeightAutoFitted = hasStoredHourHeight
export const fitHourHeightToContainer = (containerEl, hourSpan) => {
  if (hourHeightAutoFitted || !containerEl || !hourSpan) return
  hourHeightAutoFitted = true
  setHourHeight(containerEl.clientHeight / hourSpan)
}

// Pointer-drag handler for a resize handle — pass directly to @pointerdown
export const startHourHeightResize = event => {
  event.preventDefault()
  event.target.setPointerCapture(event.pointerId)
  const startY = event.clientY
  const startHeight = hourHeight.value
  document.body.style.cursor = 'ns-resize'
  document.body.style.userSelect = 'none'
  const onMove = e => setHourHeight(startHeight + (e.clientY - startY))
  const onEnd = () => {
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onEnd)
    window.removeEventListener('pointercancel', onEnd)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onEnd)
  window.addEventListener('pointercancel', onEnd)
}

// ── Week helpers (pure) ──────────────────────────────────────────────────────
export const localIso = d => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export const easternToday = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date())
  const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]))
  return new Date(+p.year, +p.month - 1, +p.day)
}

export const getMonday = d => {
  const date = new Date(d)
  const dow = date.getDay()
  date.setDate(date.getDate() - (dow === 0 ? 6 : dow - 1))
  date.setHours(0, 0, 0, 0)
  return date
}

// ── Time math (pure) ─────────────────────────────────────────────────────────
export const parseLessonTime = t => {
  if (!t) return null
  const ampm = t.match(/(\d+):(\d+)\s*(AM|PM)/i)
  if (ampm) {
    let h = parseInt(ampm[1], 10)
    const m = parseInt(ampm[2], 10)
    if (ampm[3].toUpperCase() === 'PM' && h !== 12) h += 12
    if (ampm[3].toUpperCase() === 'AM' && h === 12) h = 0
    return { h, m }
  }
  const parts = t.split(':').map(Number)
  return { h: parts[0], m: parts[1] || 0 }
}

export const hourToY      = (h, minHour)  => (h - minHour) * hourHeight.value
export const timeToY      = (t, minHour)  => { const p = parseLessonTime(t); return p ? ((p.h + p.m / 60) - minHour) * hourHeight.value : 0 }
export const durationToH  = mins          => (mins / 60) * hourHeight.value

// Inverse of hourToY — pixel Y offset within the grid body back to a "HH:MM" time,
// snapped to the nearest 15 minutes. Used by click-to-create-lesson handlers.
export const yToTime = (y, minHour) => {
  const totalMinutes = Math.round(((y / hourHeight.value) + minHour) * 60 / 15) * 15
  const clamped = Math.max(0, Math.min(23 * 60 + 45, totalMinutes))
  const h = Math.floor(clamped / 60)
  const m = clamped % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// ── Format helpers (pure) ────────────────────────────────────────────────────
export const fmtHour = h => `${h % 12 || 12}${h >= 12 ? 'pm' : 'am'}`

export const fmtTime = t => {
  const p = parseLessonTime(t)
  if (!p) return '?'
  const suffix = p.h >= 12 ? 'pm' : 'am'
  const hour   = p.h % 12 || 12
  return p.m ? `${hour}:${String(p.m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`
}

export const fmtDuration = mins => {
  if (!mins) return '30m'
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60), m = mins % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

// ── Status helpers (pure) ────────────────────────────────────────────────────
const STATUS_COLOR = { attended: '#16a34a', absent: '#dc2626', excused: '#d97706', scheduled: '#2563eb' }

export const lessonStatus    = lesson => { const first = (lesson.attendance || [])[0]; return (first?.attendance || first?.status || 'none').toLowerCase() }
export const statusDotColor  = status => STATUS_COLOR[status] || '#9ca3af'

// Lesson-level status, mirroring the backend's get_attendance_status priority
// (attended > excused > absent > not reported > scheduled) rather than just the
// first attendee — needed for group lessons where entries can disagree.
export const isLessonExcused = lesson => {
  const statuses = (lesson.attendance || []).map(s => (s.attendance || s.status || 'not reported').toLowerCase())
  if (!statuses.length) return false
  if (statuses.includes('attended')) return false
  return statuses.includes('excused') || statuses.includes('snow day')
}

// Mirrors the backend's lesson_lock_tier() (lesson_billing.py) — the single
// source of truth for how locked a lesson is, shared by every edit/delete
// surface. 'paid': nobody may edit/clone/delete. 'locked': dispute window
// closed but nothing charged yet, admins may still act. 'open': unrestricted.
const PAID_BILLING_STATUSES = new Set(['succeeded', 'submitted', 'processing'])
export const lessonLockTier = lesson => {
  if (PAID_BILLING_STATUSES.has(lesson.billing_status)) return 'paid'
  if (lesson.dispute_window_cleared) return 'locked'
  return 'open'
}

// ── Cross-view change signal ─────────────────────────────────────────────────
// Every mutation (edit/delete/clone/create/attendance) calls notifyLessonsChanged();
// every lesson-displaying view watches lessonsChange and refetches, so no view keeps
// serving stale data after a save — including views that don't own the edited card
// (Team Calendar, the teacher dashboard's list/attendance panes) and lessons a
// series-scope edit cascaded to that the clicked card never saw.
export const lessonsChange = ref({ n: 0, attendanceOnly: false })
export const notifyLessonsChanged = ({ attendanceOnly = false } = {}) => {
  lessonsChange.value = { n: lessonsChange.value.n + 1, attendanceOnly }
}

// ── Composable ───────────────────────────────────────────────────────────────
export function useCalendarData(options = {}) {
  const lessonEndpoint = options.lessonEndpoint ?? '/api/admin/calendar'
  const fetchTeachers  = options.fetchTeachers  ?? true
  const { fetchApi } = useApi()

  const lessons       = ref([])
  const teachers      = ref([])
  const cancellations = ref([])  // marked time-off windows: [{ teacher_name, start_datetime, end_datetime, reason }]
  const isLoading     = ref(false)

  const weekStart = ref(getMonday(easternToday()))
  const todayIso  = localIso(easternToday())

  const weekDays = computed(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart.value)
      d.setDate(d.getDate() + i)
      const iso = localIso(d)
      return {
        iso,
        weekday:     d.toLocaleDateString('en-US', { weekday: 'short' }),
        weekdayLong: d.toLocaleDateString('en-US', { weekday: 'long' }),
        dayNum:      d.getDate(),
        month:       d.toLocaleDateString('en-US', { month: 'short' }),
        isToday:     iso === todayIso,
      }
    })
  )

  const weekLabel = computed(() => {
    const s = weekStart.value
    const e = new Date(s); e.setDate(e.getDate() + 6)
    const year = e.getFullYear()
    if (s.getMonth() === e.getMonth())
      return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()}–${e.getDate()}, ${year}`
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${year}`
  })

  const prevWeek = () => { const d = new Date(weekStart.value); d.setDate(d.getDate() - 7); weekStart.value = d }
  const nextWeek = () => { const d = new Date(weekStart.value); d.setDate(d.getDate() + 7); weekStart.value = d }
  const goToday  = () => { weekStart.value = getMonday(easternToday()) }

  const teacherMap = computed(() => new Map(teachers.value.map(t => [t.name, t])))

  const allTeachers = computed(() => {
    const names = new Set([
      ...teachers.value.map(t => t.name),
      ...lessons.value.map(l => l.teacher_name).filter(Boolean),
    ])
    return [...names].sort()
  })

  const allInstruments = computed(() => {
    const insts = new Set()
    teachers.value.forEach(t => (t.instruments || []).forEach(i => insts.add(i)))
    return [...insts].sort()
  })

  const allLocations = computed(() => {
    const locs = new Set()
    lessons.value.forEach(l => { if (l.location) locs.add(l.location) })
    const sorted = [...locs].sort()
    if (lessons.value.some(l => !l.location)) sorted.unshift('__none__')
    return sorted
  })

  const lessonMap = computed(() => {
    const map = new Map()
    for (const l of lessons.value) {
      const key = `${l.teacher_name}::${l.lesson_date}`
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(l)
    }
    for (const arr of map.values())
      arr.sort((a, b) => (a.lesson_time || '').localeCompare(b.lesson_time || ''))
    return map
  })

  const lessonsFor = (teacher, iso) => lessonMap.value.get(`${teacher}::${iso}`) || []

  const locationLessonMap = computed(() => {
    const map = new Map()
    for (const l of lessons.value) {
      const loc = l.location || '__none__'
      const key = `${loc}::${l.lesson_date}`
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(l)
    }
    for (const arr of map.values())
      arr.sort((a, b) => (a.lesson_time || '').localeCompare(b.lesson_time || ''))
    return map
  })

  const lessonsForLocation = (loc, iso) => locationLessonMap.value.get(`${loc}::${iso}`) || []

  // Clip a (possibly multi-day) cancellation window down to "HH:MM"–"HH:MM" for one
  // specific calendar day, for DayTimeGrid's timeOffBlocks prop. Returns null if the
  // window doesn't actually touch this day.
  const _clipToDay = (c, iso) => {
    const dayStart = new Date(`${iso}T00:00:00`)
    const dayEnd   = new Date(`${iso}T23:59:59`)
    const start = new Date(c.start_datetime)
    const end   = new Date(c.end_datetime)
    if (end <= dayStart || start >= dayEnd) return null
    const clipStart = start < dayStart ? dayStart : start
    const clipEnd   = end > dayEnd ? dayEnd : end
    const fmt = d => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    return { id: c.id, start: fmt(clipStart), end: end > dayEnd ? '23:59' : fmt(clipEnd), teacherName: c.teacher_name, reason: c.reason }
  }

  // All teachers' time-off blocks touching this day (for a merged/team column).
  const timeOffBlocksForDay = iso =>
    cancellations.value.map(c => _clipToDay(c, iso)).filter(Boolean)

  // One teacher's time-off blocks for this day (for a locked-teacher column).
  const timeOffBlocksFor = (teacherName, iso) =>
    timeOffBlocksForDay(iso).filter(b => b.teacherName === teacherName)

  let refetchQueued = false
  const fetchData = async () => {
    // A call made mid-fetch (e.g. a save landing while the previous load is in flight)
    // used to be dropped, leaving the view on pre-save data — re-run once instead.
    if (isLoading.value) { refetchQueued = true; return }
    isLoading.value = true
    try {
      const start = weekDays.value[0].iso
      const end   = weekDays.value[6].iso
      const [lessonData, teacherData, cancellationData] = await Promise.all([
        fetchApi(`${lessonEndpoint}?start_date=${start}&end_date=${end}`),
        fetchTeachers && !teachers.value.length ? fetchApi('/api/teacher-availability/roster') : Promise.resolve(teachers.value),
        fetchApi(`/api/teacher-cancellations/range?start_date=${start}T00:00:00&end_date=${end}T23:59:59`).catch(() => []),
      ])
      lessons.value       = lessonData
      teachers.value      = teacherData
      cancellations.value = cancellationData
    } catch {
      console.error('Failed to fetch calendar data')
    } finally {
      isLoading.value = false
      if (refetchQueued) { refetchQueued = false; fetchData() }
    }
  }

  watch(lessonsChange, () => fetchData())

  // Drops locally-known lessons (e.g. ones soft-deleted by an "end series" edit)
  // without a refetch — see cancelled_lesson_ids returned from /lesson-edits.
  const removeLessons = (ids) => {
    if (!ids?.length) return
    const idSet = new Set(ids)
    lessons.value = lessons.value.filter(l => !idSet.has(l.id))
  }

  // Drops a locally-known cancellation (e.g. after the user deletes it from the
  // calendar) without a refetch — same convention as removeLessons above.
  const removeCancellation = (id) => {
    cancellations.value = cancellations.value.filter(c => c.id !== id)
  }

  return {
    lessons, teachers, cancellations, isLoading,
    weekStart, todayIso, weekDays, weekLabel,
    prevWeek, nextWeek, goToday,
    teacherMap, allTeachers, allInstruments, allLocations,
    lessonMap, lessonsFor, locationLessonMap, lessonsForLocation,
    timeOffBlocksForDay, timeOffBlocksFor,
    fetchData, removeLessons, removeCancellation,
  }
}
